import { Link, useRouterState } from "@tanstack/react-router"
import { Menu } from "lucide-react"
import { motion } from "motion/react"
import { useEffect, useState } from "react"
import { BrandMark } from "@/components/ui/brand-mark"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SharedButton } from "@/components/ui/shared-button"
import { cn } from "@/lib/utils"

type NavItem = { to: string; label: string }

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { to: "/comics", label: "Comics" },
  { to: "/gallery", label: "Gallery" },
  { to: "/auteur", label: "Auteur" },
]

export function Navbar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "fixed inset-x-0 top-0 z-50 h-20 border-white/[0.04] border-b bg-background/40 backdrop-blur-md transition-opacity duration-300",
          scrolled ? "opacity-100" : "opacity-0"
        )}
      />
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 left-4 z-50 sm:left-6 md:left-8"
      >
        <BrandMark />
      </motion.div>

      <motion.nav
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 left-1/2 z-50 hidden w-fit max-w-[96vw] -translate-x-1/2 sm:block"
        aria-label="Primary"
      >
        <div className="flex h-11 items-center p-1">
          <ul className="flex h-full items-center gap-0.5 font-mono text-[14px]">
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.to
              return (
                <li key={item.to} className="flex h-full items-center">
                  <Link
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "relative flex h-9 items-center rounded-none px-4 font-semibold transition-colors duration-200",
                      active
                        ? "text-brand after:absolute after:right-4 after:bottom-1.5 after:left-4 after:h-px after:bg-brand"
                        : "text-white/70 hover:text-brand"
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      </motion.nav>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 right-4 z-50 hidden sm:right-6 sm:block md:right-8"
      >
        <SharedButton to="/contact">Get in touch</SharedButton>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-4 right-4 z-50 sm:hidden"
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Open menu"
            className="flex size-11 items-center justify-center text-white/85 transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Menu size={18} aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="w-56 rounded-none border-white/15 bg-background/85 backdrop-blur-xl"
          >
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.to
              return (
                <DropdownMenuItem key={item.to} asChild>
                  <Link
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "w-full cursor-pointer font-mono font-semibold text-[12px]",
                      active &&
                        "bg-brand/15 text-brand focus:bg-brand/20 focus:text-brand"
                    )}
                  >
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem asChild>
              <Link
                to="/contact"
                className="w-full cursor-pointer font-mono font-semibold text-[12px] text-brand focus:bg-brand/15 focus:text-brand"
              >
                Get in touch
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </motion.div>
    </>
  )
}
